import type { SupabaseClient } from '@supabase/supabase-js';
import { openModal, showToast } from '@vario-connect/frontend-design';
import { pinModalToOpener } from '../lib/modal-anchor.js';
import { normalizeText, normalizeZip } from '../lib/address-normalize.js';
import { ensureOttoStyles, OTTO_ICON, ottoEscapeHtml } from './otto-ui.js';
import { interpretSettingsHealth, type HealthInput } from './otto-settings-health.js';
import { httpErrorText } from '../lib/session-expiry.js';

interface SettingsContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

interface StorefrontRow {
  storefront: string;
  vario_channel_id: string;
  vario_channel_label: string;
  vario_storage_id?: string | null;       // Standard-Lager (= Vario defaultSalesStorage)
  vario_storage_label?: string | null;
  vario_storage_ids?: string[] | null;     // alle gewählten Lager (Mehrfachauswahl)
  vario_price_channel_id?: string | null;    // Standard-Preis-Kanal (NULL → Fallback vario_channel_id)
  vario_price_channel_label?: string | null;
  // Auftragsübernahme (0303): Vario-Pflichtmodalitäten + Auto-Übernahme-Flag.
  vario_payment_method_id?: string | null;
  vario_payment_method_label?: string | null;
  vario_payment_term_id?: string | null;
  vario_payment_term_label?: string | null;
  vario_delivery_method_id?: string | null;
  vario_delivery_method_label?: string | null;
  vario_delivery_term_id?: string | null;
  vario_delivery_term_label?: string | null;
  auto_takeover_orders?: boolean | null;
}

interface AddResponse {
  ok?: boolean;
  storefront?: string;
  label?: string;
  channelId?: string;
  alreadyPresent?: boolean;
  error?: { code: string; message: string };
}

interface Warehouse {
  id: string;
  number: string | null;
  displayName: string | null;
  salesStorage: boolean | null;
}

// Design-Handoff „Otto Einstellungen.html": Empty-State-Kreis-Icon, Lager-Reload-
// Pfeil und die fuenf „in Vorbereitung"-Kachel-Icons. stroke-basiert, currentColor.
const ICON = {
  channel:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M3 9h18M9 21V9"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>',
  reload:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12a9 9 0 1 1-6.2-8.5"/><path d="M21 3v6h-6"/></svg>',
  catSync:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><path d="M7 7h.01"/></svg>',
  shipping:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 18V6a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h2"/><path d="M14 9h4l3 3v5a1 1 0 0 1-1 1h-1"/><circle cx="7.5" cy="18.5" r="1.8"/><circle cx="17.5" cy="18.5" r="1.8"/></svg>',
  delivery:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>',
  payMethod:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>',
  payTerm:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>',
  save:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>',
};

// „In Vorbereitung"-Kacheln. Versandart/Lieferbedingung/Zahlart/Zahlungsbedingung sind jetzt real
// im Abschnitt „Auftragsübernahme" umgesetzt → entfernt. Leer = der ganze Block wird ausgeblendet.
const SOON_ITEMS: ReadonlyArray<{ icon: string; title: string; sub: string }> = [];

/**
 * Tab „Einstellungen" (system.apps.{appId}.tabs → /#/settings). Otto = genau EIN Marktplatz
 * (otto.de) → ein Vario-Verkaufskanal + ein zugeordnetes Vario-Lager. Anlegen/Status via
 * otto-sales-channel-add + otto_list_storefronts; Lager via vario-warehouses-list (marketplace
 * 'otto') + otto_set_storefront_warehouse; Entfernen (DB-only, 2-Klick) via otto_remove_storefront.
 * Layout nach Design-Handoff „Otto Einstellungen.html" (konsolidierte Fassung): KEIN Karten-Kopf
 * und KEINE grüne „aktiv"-Box mehr (dupliziert den Header bzw. die Fußzeile) — die Karte beginnt
 * direkt mit Intro + „Vario-Verkaufskanal"-Abschnitt. Empty-State mit CTA, Aktiv-Form mit
 * Read-only-Kanalfeldern, Lager-Auswahl und „in Vorbereitung"-Kacheln. Fußzeile: Status +
 * „Verknüpfung lösen" (2-Klick) + „Speichern" (speichert die Lagerzuordnung explizit).
 * Verkaufskanal-Name ist READ-ONLY (Vario-Wert; es gibt keinen Rename-RPC). KEINE Browser-Dialoge.
 */
export async function renderOttoSettings(ctx: SettingsContext) {
  ensureOttoStyles();
  ctx.contentEl.hidden = false;
  // Reines Auto-Grow (main.ts 'auto', Max=Infinity): die bridge-§16-Kette
  // (html,body,main#app = height:100% + overflow:hidden) wuerde body.scrollHeight
  // auf die Viewport-Hoehe deckeln. Dieser Marker loest die Kette NUR im Settings-
  // iframe (eigener Slot-Load) per hoeher-spezifischem Override im Style-Block unten.
  document.documentElement.classList.add('otto-autogrow');

  const soonGrid = SOON_ITEMS.map(
    (it) =>
      `<div class="soon-item"><span class="si-ic">${it.icon}</span><div><div class="si-t">${it.title}</div><div class="si-s">${it.sub}</div></div></div>`,
  ).join('');

  ctx.contentEl.innerHTML = `
    <div class="otto-install otto-set">
      <header class="apphead">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce"><img src="/logo-black.png" alt="go eCommerce"></a>
        <img class="otto-logo" src="/otto-market.png" alt="OTTO Market">
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">OTTO · Einstellungen</div><h1>Marktplatz-Einstellungen</h1></div>
        <div class="hactions">
          <span class="hstatus"><span class="d"></span>OTTO Market verbunden</span>
        </div>
      </header>
      <div class="stage">
        <div class="card" id="sc-card">
          <div class="card-body">
            <p class="intro">Lege hier den zugehörigen <b>Verkaufskanal in Vario</b> an und ordne ihm ein <b>Vario-Lager</b> zu. Anschließend kannst du diverse Einstellungen rund um Otto treffen.</p>

            <div class="sec-lab">Vario-Verkaufskanal</div>

            <!-- Lade-Zustand -->
            <div id="sc-loading"><span class="sc-loading">${OTTO_ICON.info}Status wird geladen …</span></div>

            <!-- Leer-Zustand -->
            <div class="empty" id="sc-empty" hidden>
              <div class="empty-ic">${ICON.channel}</div>
              <h3>Noch kein Vario-Verkaufskanal für OTTO angelegt</h3>
              <p>Verknüpfe einen Kanal mit einem Lager, um Bestände, Preise und Bestellungen zu synchronisieren.</p>
              <button class="btn btn-primary" id="sc-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>Verkaufskanal anlegen</button>
            </div>

            <!-- Aktiv-Zustand -->
            <div class="chan" id="sc-form" hidden>
              <div class="fieldrow">
                <div class="field">
                  <label for="sc-name">Verkaufskanal-Name</label>
                  <input class="inp" id="sc-name" readonly>
                </div>
                <div class="field">
                  <label for="sc-id">Channel-ID</label>
                  <input class="inp mono" id="sc-id" readonly>
                </div>
              </div>

              <div class="field">
                <label>Vario-Lager <span class="req">*</span></label>
                <div class="lager-row">
                  <div class="selwrap"><div class="wh-list" id="wh-list"><span class="wh-empty">— Lager werden geladen …</span></div></div>
                  <button class="reload" id="wh-reload" title="Lager neu laden" type="button">${ICON.reload}</button>
                </div>
                <span class="hint">Wähle ein oder mehrere Vario-Lager. Aus den markierten Lagern wird der Bestand ermittelt (<b>Summe</b>); das <b>Standard-Lager</b> ist die Vorgabe des Verkaufskanals (Vario-Default). Die markierten Lager sind im Artikel-Listing vorausgewählt.</span>
                <span class="foot-status" id="wh-status" style="margin-top:8px;"></span>
              </div>

              <div class="sec-lab" style="margin-top:24px;">Herkunft des Otto-Preises</div>
              <div class="field">
                <label for="pc-select">Preis-Verkaufskanal</label>
                <div class="lager-row">
                  <div class="selwrap" style="flex:1;">
                    <select class="inp" id="pc-select"><option value="">— Kanäle werden geladen …</option></select>
                  </div>
                  <button class="reload" id="pc-reload" title="Kanäle neu laden" type="button">${ICON.reload}</button>
                </div>
                <span class="hint">Dieser Preis dient als Preis Grundlage für neue Listings. Bei einem neuen Listing wird anhand des Preises der Otto Verkaufskanal Preis gesetzt.</span>
                <span class="foot-status" id="pc-status" style="margin-top:8px;"></span>
              </div>

              <div class="sec-lab" style="margin-top:24px;">Bestandsübertragung</div>
              <label class="push-toggle" id="ap-wrap" title="Verfügbarer Bestand wird automatisch an OTTO gesendet, sobald er sich in VARIO ändert">
                <input type="checkbox" id="ap-toggle" disabled />
                <span class="push-toggle-txt">
                  <b>Bestand automatisch an OTTO senden</b>
                  <span class="push-toggle-sub">Ändert sich der Bestand in VARIO, geht er automatisch an OTTO, nach Min/Max, sonst der verfügbare Bestand 1:1. Ohne Haken geht kein Bestand automatisch an OTTO, auch nicht über den regelmäßigen Abgleich. Senden lässt er sich dann nur noch über „Aktualisieren“ in der Angebotsübersicht.</span>
                </span>
              </label>
              <span class="foot-status" id="ap-status" style="margin-top:6px;"></span>

              <div class="sec-lab" style="margin-top:24px;">Preisübertragung</div>
              <label class="push-toggle" id="pp-wrap" title="Kanalpreis wird automatisch an OTTO gesendet, sobald er sich in VARIO ändert">
                <input type="checkbox" id="pp-toggle" disabled />
                <span class="push-toggle-txt">
                  <b>Preis automatisch an OTTO senden</b>
                  <span class="push-toggle-sub">Ändert sich der Preis im OTTO-Kanal in VARIO, geht er automatisch an OTTO. Ohne Haken geht kein Preis automatisch an OTTO, auch nicht über den regelmäßigen Abgleich. Senden lässt er sich dann nur noch über „Aktualisieren“ in der Angebotsübersicht oder über eine Preisänderung direkt in der Liste.</span>
                </span>
              </label>
              <span class="foot-status" id="pp-status" style="margin-top:6px;"></span>

              <div class="sec-lab" style="margin-top:24px;">Auftragsübernahme</div>
              <span class="hint">Beim Übernehmen einer OTTO-Bestellung als Vario-Kundenauftrag müssen Zahlart, Zahlungsbedingung, Versandart und Lieferbedingung gesetzt sein (im Vario-Schema Pflicht).</span>
              <div class="fieldrow" style="margin-top:10px;">
                <div class="field">
                  <label for="ot-pm">Zahlart <span class="req">*</span></label>
                  <select class="inp" id="ot-pm" disabled><option value="">— wird geladen …</option></select>
                </div>
                <div class="field">
                  <label for="ot-pt">Zahlungsbedingung <span class="req">*</span></label>
                  <select class="inp" id="ot-pt" disabled><option value="">— wird geladen …</option></select>
                </div>
              </div>
              <div class="fieldrow">
                <div class="field">
                  <label for="ot-dm">Versandart <span class="req">*</span></label>
                  <select class="inp" id="ot-dm" disabled><option value="">— wird geladen …</option></select>
                </div>
                <div class="field">
                  <label for="ot-dt">Lieferbedingung <span class="req">*</span></label>
                  <select class="inp" id="ot-dt" disabled><option value="">— wird geladen …</option></select>
                </div>
              </div>
              <div class="lager-row">
                <span class="foot-status" id="ot-status" style="flex:1;"></span>
                <button class="reload" id="ot-reload" title="Vario-Optionen neu laden" type="button">${ICON.reload}</button>
              </div>
              <label class="push-toggle" id="at-wrap" style="margin-top:12px;" title="Vollständig zugeordnete Bestellungen werden automatisch als Vario-Kundenauftrag übernommen">
                <input type="checkbox" id="at-toggle" disabled />
                <span class="push-toggle-txt">
                  <b>Bestellungen automatisch übernehmen</b>
                  <span class="push-toggle-sub">Offene Bestellungen, die nach dem Setzen des Hakens eingehen, werden als Vario-Kundenauftrag angelegt, sobald alle Positionen zugeordnet sind (nur wenn die vier Felder oben gesetzt sind). Ältere, versendete oder stornierte Bestellungen bleiben unberührt. Ohne Haken erfolgt die Übernahme nur manuell im Bestelldetail.</span>
                </span>
              </label>
              <span class="foot-status" id="at-status" style="margin-top:6px;"></span>

              <div class="sec-lab" style="margin-top:24px;">Versand</div>
              <span class="hint">Absenderadresse für Versandmeldungen an OTTO. OTTO verlangt sie bei jedem Versand. Der automatische Versand aus Vario nimmt die Absenderadresse der Vario-Sendung und greift nur ohne sie auf diese Angabe zurück; der manuelle Versand im Reiter Bestellungen nutzt immer diese Angabe.</span>
              <div class="fieldrow" style="margin-top:10px;grid-template-columns:1fr 2fr 1fr;">
                <div class="field">
                  <label for="sf-zip">PLZ <span class="req">*</span></label>
                  <input class="inp" id="sf-zip" maxlength="10" autocomplete="off" disabled>
                </div>
                <div class="field">
                  <label for="sf-city">Ort <span class="req">*</span></label>
                  <input class="inp" id="sf-city" maxlength="60" autocomplete="off" disabled>
                </div>
                <div class="field">
                  <label for="sf-country">Land <span class="req">*</span></label>
                  <input class="inp mono" id="sf-country" maxlength="3" placeholder="DEU" autocomplete="off" disabled>
                </div>
              </div>
              <span class="hint">Land als dreistelliger Ländercode (ISO 3166 alpha-3), z. B. DEU, AUT. Gespeichert wird beim Verlassen eines Feldes.</span>
              <span class="foot-status" id="sf-status" style="margin-top:6px;"></span>

              <div class="sec-lab" style="margin-top:24px;">Kategorien</div>
              <div class="soon-item" style="border-style:solid;">
                <span class="si-ic">${ICON.catSync}</span>
                <div style="flex:1;">
                  <div class="si-t">Kategorie-Sync</div>
                  <div class="si-s">Ausgewählte OTTO-Kategoriegruppen samt ihren Kategorien als Vario-Kategoriebaum übernehmen</div>
                  <span class="foot-status" id="cat-sync-status" style="margin-top:6px;"></span>
                </div>
                <button class="btn btn-ghost" id="cat-select-btn" type="button">${ICON.catSync}Kategoriegruppen auswählen</button>
              </div>

              <div class="field" style="margin-top:14px;">
                <label for="cm-select">Kategorie bei der Zuordnung eines OTTO-Angebots</label>
                <select class="inp" id="cm-select" disabled>
                  <option value="take">Kategorie aus OTTO übernehmen</option>
                  <option value="skip">Kategorie nicht übernehmen</option>
                  <option value="ask">Bei der Zuordnung fragen</option>
                </select>
                <span class="hint">Gilt, wenn du ein bestehendes OTTO-Angebot einem VARIO-Artikel zuordnest, der noch keine OTTO-Kategorie hat. Übernommen wird die Kategorie, die das Angebot bei OTTO hat. Ist ihre Kategoriegruppe oben noch nicht ausgewählt, wird sie automatisch ausgewählt und in VARIO übernommen. Eine schon gesetzte Kategorie bleibt unverändert.</span>
                <span class="foot-status" id="cm-status" style="margin-top:8px;"></span>
              </div>

              <div class="sec-lab" style="margin-top:24px;">Marken</div>
              <div class="soon-item" style="border-style:solid;">
                <span class="si-ic"><span class="v-icon fa-light fa-tag"></span></span>
                <div style="flex:1;">
                  <div class="si-t">Präferierte Marken</div>
                  <div class="si-s">Marken für die schnelle Auswahl im Lister festlegen</div>
                  <span class="foot-status" id="brand-sel-status" style="margin-top:6px;"></span>
                </div>
                <button class="btn btn-ghost" id="brand-select-btn" type="button">Marken auswählen</button>
              </div>

              <div class="field" style="margin-top:14px;">
                <label for="bm-select">Marke bei der Zuordnung eines OTTO-Angebots</label>
                <select class="inp" id="bm-select" disabled>
                  <option value="take">Marke aus OTTO übernehmen</option>
                  <option value="skip">Marke nicht übernehmen</option>
                  <option value="ask">Bei der Zuordnung fragen</option>
                </select>
                <span class="hint">Gilt, wenn du ein bestehendes OTTO-Angebot einem VARIO-Artikel zuordnest und der Artikel im Lister noch keine Marke hat. Übernommen wird die Marke, die das Angebot bei OTTO hat. Ist sie oben noch keine präferierte Marke, wird sie automatisch aufgenommen. Eine schon gesetzte Marke bleibt unverändert.</span>
                <span class="foot-status" id="bm-status" style="margin-top:8px;"></span>
              </div>

              ${SOON_ITEMS.length > 0 ? `<div class="sec-lab" style="margin-top:24px;">Weitere Einstellungen <span class="soon">in Vorbereitung</span></div>
              <div class="soon-grid">${soonGrid}</div>` : ''}
            </div>
          </div>

          <div class="card-foot">
            <span class="foot-status" id="sc-foot">${OTTO_ICON.info}Verkaufskanal noch nicht angelegt</span>
            <div class="foot-actions">
              <button class="btn btn-ghost" id="sc-remove" hidden>${OTTO_ICON.trash}Verknüpfung lösen</button>
              <button class="btn btn-primary" id="sc-save" hidden>${ICON.save}Speichern</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>
      /* hidden MUSS gewinnen: Klassen-Regeln wie .empty (display:flex) /
         .btn (display:inline-flex) haben hoehere Specificity als die Browser-Default
         [hidden]=display:none → ohne diesen Reset blieb der Empty-State (#sc-empty.empty)
         sichtbar, obwohl renderActive() hidden=true setzt (Aktiv-Form + "kein Verkaufskanal"
         gleichzeitig). Scoped + !important deckt alle Toggle-Elemente ab. */
      .otto-install [hidden]{display:none !important;}
      /* Settings-Shell: reines Auto-Grow (main.ts 'auto', Max=Infinity). iframe =
         Inhaltshoehe, KEIN interner Scroll — Vario scrollt aussen. Voraussetzung ist
         der §16-Override unten, sonst deckelt bridge.css body.scrollHeight auf 100%. */
      /* bridge.css §16 zwingt html,body,main#app auf height:100% + overflow:hidden →
         body.scrollHeight = Viewport statt Inhalt. Nur im Settings-iframe (eigener
         Slot-Load) loesen wir die Kette: hoehere Specificity (html.otto-autogrow…)
         gewinnt gegen die nackten bridge-Selektoren, ohne die geteilte Datei zu aendern. */
      html.otto-autogrow, html.otto-autogrow body, html.otto-autogrow main#app{height:auto;overflow:visible;}
      html.otto-autogrow main#app > section#content{flex:0 0 auto;}
      .otto-install.otto-set{box-sizing:border-box;display:flex;flex-direction:column;height:auto;padding:16px 26px 20px;}
      .otto-install.otto-set .apphead{flex:none;margin-bottom:16px;}
      .otto-install.otto-set .otto-logo{height:24px;width:auto;flex:none;}
      .otto-install.otto-set .hactions{margin-left:auto;display:flex;gap:13px;align-items:center;}
      .otto-install.otto-set .hstatus{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;color:var(--muted);font-weight:500;}
      .otto-install.otto-set .hstatus .d{width:8px;height:8px;border-radius:50%;background:var(--success);box-shadow:0 0 0 3px rgba(34,197,94,.16);}
      .otto-install.otto-set .stage{display:flex;flex-direction:column;align-items:flex-start;justify-content:flex-start;padding:2px 4px;}
      .otto-install.otto-set .card{align-self:flex-start;}
      .otto-install .sc-loading{display:inline-flex;align-items:center;gap:8px;font-size:12.5px;color:var(--muted);}
      .otto-install .empty{display:flex;flex-direction:column;align-items:center;text-align:center;padding:14px 20px 6px;}
      .otto-install .empty-ic{width:84px;height:84px;border-radius:50%;background:var(--vario-green-tint);display:flex;align-items:center;justify-content:center;margin-bottom:18px;}
      .otto-install .empty-ic svg{width:38px;height:38px;color:var(--vario-green-dark);}
      .otto-install .empty h3{font-size:16px;font-weight:600;margin:0 0 7px;color:var(--ink);}
      .otto-install .empty p{font-size:13.5px;color:var(--muted);margin:0 auto 20px;max-width:360px;line-height:1.6;}
      .otto-install .fieldrow{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
      .otto-install .inp[readonly]{background:#FAFAFA;color:var(--ink-soft);cursor:default;}
      .otto-install .inp[readonly]:focus{border-color:#E2E2E6;box-shadow:none;}
      .otto-install .lager-row{display:flex;gap:10px;align-items:flex-start;}
      .otto-install .lager-row .selwrap{flex:1;}
      .otto-install .wh-list{display:flex;flex-direction:column;gap:2px;border:1px solid #E2E2E6;border-radius:9px;padding:6px;max-height:240px;overflow-y:auto;background:#fff;}
      .otto-install .wh-empty{font-size:12.5px;color:var(--muted);padding:8px;}
      .otto-install .wh-opt{display:flex;align-items:center;gap:10px;padding:7px 8px;border-radius:7px;}
      .otto-install .wh-opt:hover{background:var(--vario-green-tint);}
      .otto-install .wh-opt .wh-pick{display:flex;align-items:center;gap:10px;flex:1;cursor:pointer;font-size:13px;color:var(--ink);min-width:0;}
      .otto-install .wh-opt .wh-name{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
      .otto-install .wh-opt .wh-sale{color:var(--muted);}
      .otto-install .wh-opt input[type=checkbox]{flex:none;width:16px;height:16px;accent-color:var(--vario-green-dark);cursor:pointer;}
      .otto-install .wh-opt .wh-def{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;color:var(--ink-soft);cursor:pointer;white-space:nowrap;flex:none;}
      .otto-install .wh-opt .wh-def input[type=radio]{width:15px;height:15px;accent-color:var(--vario-green-dark);cursor:pointer;}
      .otto-install .wh-opt.is-unchecked .wh-pick{opacity:.6;}
      .otto-install .wh-opt.is-unchecked .wh-def{opacity:.3;pointer-events:none;}
      .otto-install .push-toggle{display:flex;align-items:flex-start;gap:11px;padding:13px 14px;border:1px solid #E2E2E6;border-radius:10px;background:#fff;cursor:pointer;transition:border-color .15s;}
      .otto-install .push-toggle:hover{border-color:var(--vario-green);}
      .otto-install .push-toggle input[type=checkbox]{flex:none;width:17px;height:17px;margin-top:1px;accent-color:var(--vario-green-dark);cursor:pointer;}
      .otto-install .push-toggle-txt{display:flex;flex-direction:column;gap:3px;font-size:13px;color:var(--ink);}
      .otto-install .push-toggle-sub{font-size:11.5px;color:var(--muted);line-height:1.5;}
      .otto-install .reload{width:44px;height:44px;flex:none;border:1px solid #E2E2E6;background:#fff;border-radius:9px;display:flex;align-items:center;justify-content:center;color:var(--ink-soft);cursor:pointer;transition:border-color .15s,color .15s,background .15s;}
      .otto-install .reload:hover{border-color:var(--vario-green);color:var(--vario-green-dark);background:var(--vario-green-tint);}
      .otto-install .reload svg{width:17px;height:17px;}
      .otto-install .soon{font-size:10px;font-weight:600;letter-spacing:.04em;text-transform:none;color:#9A6A12;background:#FDF1DC;border-radius:999px;padding:2px 9px;}
      .otto-install .soon-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
      .otto-install .soon-item{display:flex;align-items:center;gap:12px;border:1px dashed #DEDEE2;border-radius:10px;padding:13px 14px;background:#FBFBFC;}
      .otto-install .soon-item .si-ic{width:34px;height:34px;border-radius:9px;background:#F1F1F3;display:flex;align-items:center;justify-content:center;flex:none;color:#9aa0a6;}
      .otto-install .soon-item .si-ic svg{width:17px;height:17px;}
      .otto-install .soon-item .si-t{font-size:13px;font-weight:600;color:var(--ink-soft);}
      .otto-install .soon-item .si-s{font-size:11.5px;color:#9aa0a6;margin-top:1px;}
      .otto-install #sc-remove.armed{color:var(--danger);border-color:var(--danger);}
    </style>
  `;

  const loadingEl = ctx.contentEl.querySelector<HTMLDivElement>('#sc-loading')!;
  const emptyEl = ctx.contentEl.querySelector<HTMLDivElement>('#sc-empty')!;
  const formEl = ctx.contentEl.querySelector<HTMLDivElement>('#sc-form')!;
  const footEl = ctx.contentEl.querySelector<HTMLSpanElement>('#sc-foot')!;
  const btn = ctx.contentEl.querySelector<HTMLButtonElement>('#sc-btn')!;
  const removeBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#sc-remove')!;
  const saveBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#sc-save')!;
  const nameInp = ctx.contentEl.querySelector<HTMLInputElement>('#sc-name')!;
  const idInp = ctx.contentEl.querySelector<HTMLInputElement>('#sc-id')!;
  const whList = ctx.contentEl.querySelector<HTMLDivElement>('#wh-list')!;
  const whReload = ctx.contentEl.querySelector<HTMLButtonElement>('#wh-reload')!;
  const whStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#wh-status')!;
  const apToggle = ctx.contentEl.querySelector<HTMLInputElement>('#ap-toggle')!;
  const apStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#ap-status')!;
  const ppToggle = ctx.contentEl.querySelector<HTMLInputElement>('#pp-toggle')!;
  const ppStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#pp-status')!;
  const cmSelect = ctx.contentEl.querySelector<HTMLSelectElement>('#cm-select')!;
  const cmStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#cm-status')!;
  const bmSelect = ctx.contentEl.querySelector<HTMLSelectElement>('#bm-select')!;
  const bmStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#bm-status')!;
  const pcSelect = ctx.contentEl.querySelector<HTMLSelectElement>('#pc-select')!;
  const pcReload = ctx.contentEl.querySelector<HTMLButtonElement>('#pc-reload')!;
  const pcStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#pc-status')!;
  const otPm = ctx.contentEl.querySelector<HTMLSelectElement>('#ot-pm')!;
  const otPt = ctx.contentEl.querySelector<HTMLSelectElement>('#ot-pt')!;
  const otDm = ctx.contentEl.querySelector<HTMLSelectElement>('#ot-dm')!;
  const otDt = ctx.contentEl.querySelector<HTMLSelectElement>('#ot-dt')!;
  const otStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#ot-status')!;
  const otReload = ctx.contentEl.querySelector<HTMLButtonElement>('#ot-reload')!;
  const atToggle = ctx.contentEl.querySelector<HTMLInputElement>('#at-toggle')!;
  const atStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#at-status')!;
  const sfZip = ctx.contentEl.querySelector<HTMLInputElement>('#sf-zip')!;
  const sfCity = ctx.contentEl.querySelector<HTMLInputElement>('#sf-city')!;
  const sfCountry = ctx.contentEl.querySelector<HTMLInputElement>('#sf-country')!;
  const sfStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#sf-status')!;

  const functionsBase = ctx.supabaseUrl.replace(/\/$/, '');
  let currentRow: StorefrontRow | null = null;
  let removeArmed = false;
  let removeTimer: ReturnType<typeof setTimeout> | undefined;

  function setFoot(kind: 'info' | 'ready' | 'error', html: string) {
    footEl.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    footEl.innerHTML = html;
  }
  function setWhStatus(kind: 'info' | 'ready' | 'error', html: string) {
    whStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    whStatus.innerHTML = html;
  }
  function resetRemoveBtn() {
    removeArmed = false;
    if (removeTimer) clearTimeout(removeTimer);
    removeBtn.classList.remove('armed');
    removeBtn.innerHTML = `${OTTO_ICON.trash}Verknüpfung lösen`;
  }

  // Lager-Laden in Daten (fetchWarehouses) + DOM (populateWarehouseSelect) zerlegt, damit der
  // Entry-Health-Check (runEntryHealthCheck) dieselbe Liste fuer Urteil UND Befuellung nutzt
  // und nicht doppelt fetcht. Der thin loadWarehouses (Reload-Button) komponiert beide.
  interface WhFetch { warehouses: Warehouse[] | null; authError: boolean; transientError: boolean; }

  async function fetchWarehouses(): Promise<WhFetch> {
    try {
      const res = await fetch(`${functionsBase}/functions/v1/vario-warehouses-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ marketplace: 'otto' }),
      });
      if (res.status === 401) return { warehouses: null, authError: true, transientError: false };
      const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; warehouses?: Warehouse[] };
      if (!res.ok || !payload.ok) return { warehouses: null, authError: false, transientError: true };
      return { warehouses: payload.warehouses ?? [], authError: false, transientError: false };
    } catch {
      return { warehouses: null, authError: false, transientError: true };
    }
  }

  // Mehrfachauswahl: je Lager eine Checkbox + Radio „Standard". Der Standard (= Vario
  // defaultSalesStorage) muss ein markiertes Lager sein (syncDefaultState erzwingt das).
  function renderWarehouseList(list: Warehouse[], selectedIds: string[], defaultId: string | null) {
    if (list.length === 0) {
      whList.innerHTML = `<span class="wh-empty">Keine Vario-Lager gefunden — bitte in Vario anlegen.</span>`;
      return;
    }
    const sel = new Set(selectedIds);
    const def = defaultId && sel.has(defaultId) ? defaultId : (selectedIds[0] ?? null);
    whList.innerHTML = list
      .map((w) => {
        const label = w.displayName || w.number || w.id;
        const checked = sel.has(w.id);
        return `<div class="wh-opt${checked ? '' : ' is-unchecked'}">
          <label class="wh-pick">
            <input type="checkbox" class="wh-cb" value="${ottoEscapeHtml(w.id)}" data-label="${ottoEscapeHtml(label)}"${checked ? ' checked' : ''}/>
            <span class="wh-name">${ottoEscapeHtml(label)}${w.salesStorage ? ' <span class="wh-sale">· Verkauf</span>' : ''}</span>
          </label>
          <label class="wh-def"><input type="radio" name="wh-default" class="wh-def-radio" value="${ottoEscapeHtml(w.id)}"${w.id === def ? ' checked' : ''}/>Standard</label>
        </div>`;
      })
      .join('');
    whList.querySelectorAll<HTMLInputElement>('.wh-cb').forEach((cb) =>
      cb.addEventListener('change', () => { syncDefaultState(); onWhChanged(); }));
    whList.querySelectorAll<HTMLInputElement>('.wh-def-radio').forEach((r) =>
      r.addEventListener('change', onWhChanged));
    syncDefaultState();
  }

  // Nicht markierte Lager können nicht Standard sein; ist kein Standard gesetzt, aber mind. ein
  // Lager markiert, wird das erste markierte automatisch Standard.
  function syncDefaultState() {
    const opts = Array.from(whList.querySelectorAll<HTMLDivElement>('.wh-opt'));
    let anyChecked = false;
    opts.forEach((opt) => {
      const cb = opt.querySelector<HTMLInputElement>('.wh-cb')!;
      const radio = opt.querySelector<HTMLInputElement>('.wh-def-radio')!;
      opt.classList.toggle('is-unchecked', !cb.checked);
      if (!cb.checked && radio.checked) radio.checked = false;
      if (cb.checked) anyChecked = true;
    });
    if (anyChecked && !whList.querySelector('.wh-def-radio:checked')) {
      const first = whList.querySelector<HTMLInputElement>('.wh-opt:not(.is-unchecked) .wh-def-radio');
      if (first) first.checked = true;
    }
  }

  function onWhChanged() {
    const n = getSelectedWarehouseIds().length;
    setWhStatus('info', n > 0
      ? `${OTTO_ICON.info}Noch nicht gespeichert — „Speichern" klicken`
      : `${OTTO_ICON.info}Kein Lager markiert`);
  }

  function getSelectedWarehouseIds(): string[] {
    return Array.from(whList.querySelectorAll<HTMLInputElement>('.wh-cb'))
      .filter((cb) => cb.checked).map((cb) => cb.value);
  }

  function getDefaultWarehouse(): { id: string; label: string } | null {
    const r = whList.querySelector<HTMLInputElement>('.wh-def-radio:checked');
    if (!r) return null;
    const cb = r.closest('.wh-opt')?.querySelector<HTMLInputElement>('.wh-cb');
    return { id: r.value, label: cb?.getAttribute('data-label') ?? r.value };
  }

  async function loadWarehouses(selectedIds: string[], defaultId: string | null) {
    setWhStatus('info', `${OTTO_ICON.info}Lager werden geladen …`);
    const r = await fetchWarehouses();
    if (r.authError) { setWhStatus('error', `${OTTO_ICON.info}Vario-Sitzung abgelaufen — App neu laden`); return; }
    if (!r.warehouses) { setWhStatus('error', `${OTTO_ICON.info}Lagerliste nicht ladbar`); return; }
    renderWarehouseList(r.warehouses, selectedIds, defaultId);
    const n = getSelectedWarehouseIds().length;
    setWhStatus(n > 0 ? 'ready' : 'info', n > 0
      ? `${OTTO_ICON.check}${n} Lager zugeordnet`
      : `${OTTO_ICON.info}Noch kein Lager zugeordnet`);
  }

  // Vario-Verkaufskanal-Liste fuer den Entry-Health-Check (autoritativ: fehlt der gespeicherte
  // Kanal in der Liste, wurde er in Vario geloescht → Reset). 401 = authError, sonst transient.
  interface ChFetch { channels: Array<{ id: string; label?: string }> | null; authError: boolean; transientError: boolean; }

  async function fetchVarioChannels(all = false): Promise<ChFetch> {
    try {
      const res = await fetch(`${functionsBase}/functions/v1/otto-channels-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify(all ? { all: true } : {}),
      });
      if (res.status === 401) return { channels: null, authError: true, transientError: false };
      const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; channels?: Array<{ id: string; label?: string }> };
      if (!res.ok || !payload.ok) return { channels: null, authError: false, transientError: true };
      return { channels: payload.channels ?? [], authError: false, transientError: false };
    } catch {
      return { channels: null, authError: false, transientError: true };
    }
  }

  function renderActive(row: StorefrontRow, prefetchedWarehouses?: Warehouse[]) {
    currentRow = row;
    loadingEl.hidden = true;
    emptyEl.hidden = true;
    formEl.hidden = false;
    whList.style.borderColor = ''; // evtl. stehende warehouse-invalid-Markierung zuruecksetzen
    nameInp.value = row.vario_channel_label || 'OTTO Market';
    idInp.value = row.vario_channel_id || '—';
    removeBtn.hidden = false;
    saveBtn.hidden = false;
    resetRemoveBtn();
    setFoot('ready', `${OTTO_ICON.check}Verkaufskanal angelegt &amp; verknüpft`);
    const selIds = row.vario_storage_ids ?? (row.vario_storage_id ? [row.vario_storage_id] : []);
    if (prefetchedWarehouses) {
      renderWarehouseList(prefetchedWarehouses, selIds, row.vario_storage_id ?? null);
      setWhStatus(selIds.length > 0 ? 'ready' : 'info', selIds.length > 0
        ? `${OTTO_ICON.check}${selIds.length} Lager zugeordnet`
        : `${OTTO_ICON.info}Noch kein Lager zugeordnet`);
    } else {
      void loadWarehouses(selIds, row.vario_storage_id ?? null);
    }
    void loadPriceChannel(row);
    void loadOrderTakeover(row);
    void loadShipFrom();
  }

  // Beim Betreten der Settings: Kanal- + Lager-Liste parallel holen, gegen die gespeicherten
  // IDs pruefen (interpretSettingsHealth) und je nach Urteil rendern. 'reset' = Vario-Kanal
  // autoritativ geloescht → Link entfernen + Empty-State (destruktiv NUR im bestaetigten Fall).
  async function runEntryHealthCheck(row: StorefrontRow) {
    const [ch, wh] = await Promise.all([fetchVarioChannels(), fetchWarehouses()]);
    const input: HealthInput = {
      storedChannelId: row.vario_channel_id,
      storedWarehouseId: row.vario_storage_id ?? null,
      channels: ch.channels,
      warehouses: wh.warehouses,
      authError: ch.authError || wh.authError,
      transientError: ch.transientError || wh.transientError,
    };
    const action = interpretSettingsHealth(input);

    if (action === 'reset') {
      try {
        const { error } = await ctx.supabase.rpc('otto_remove_storefront', { _storefront: 'otto' });
        if (error) throw new Error(error.message);
        renderEmpty();
        setFoot('error', `${OTTO_ICON.info}Der verknüpfte Vario-Verkaufskanal wurde in Vario gelöscht — bitte einen neuen Verkaufskanal anlegen.`);
      } catch (err) {
        // Reset-RPC fehlgeschlagen → nicht im Loading haengenbleiben: Form anzeigen + Hinweis.
        renderActive(row, wh.warehouses ?? undefined);
        setFoot('error', `${OTTO_ICON.info}Verkaufskanal in Vario gelöscht, Zurücksetzen fehlgeschlagen: ${ottoEscapeHtml((err as Error).message)} — bitte erneut öffnen.`);
      }
      return;
    }

    renderActive(row, wh.warehouses ?? undefined);

    if (action === 'reauth') {
      setFoot('error', `${OTTO_ICON.info}Vario-Sitzung abgelaufen — bitte die App neu laden/neu verbinden.`);
    } else if (action === 'transient') {
      setFoot('error', `${OTTO_ICON.info}Verbindung zu Vario fehlgeschlagen — bitte später erneut.`);
    } else if (action === 'warehouse-invalid') {
      setWhStatus('error', `${OTTO_ICON.info}Standard-Lager in Vario nicht mehr vorhanden — bitte neu wählen.`);
      // Rote Markierung der Liste: keine gestylte .invalid-Klasse im Design-System
      // (geprueft) → Inline-Border auf den Negative-Token, ohne neue Klasse zu erfinden.
      whList.style.borderColor = 'var(--v-negative, #c84a4a)';
    }
  }

  function renderEmpty() {
    loadingEl.hidden = true;
    formEl.hidden = true;
    emptyEl.hidden = false;
    removeBtn.hidden = true;
    saveBtn.hidden = true;
    setFoot('info', `${OTTO_ICON.info}Verkaufskanal noch nicht angelegt`);
  }

  // Lager-Wechsel speichert NICHT automatisch — Checkbox-/Radio-Änderungen melden nur „ungespeichert"
  // (onWhChanged, in renderWarehouseList gebunden). Der „Speichern"-Button ist der explizite Trigger.

  // „Speichern": schreibt die Mehrfach-Lagerauswahl + Standard-Lager (otto_set_storefront_warehouse).
  // Der Kanalname ist read-only (Vario-Wert) → nur die Lager werden persistiert.
  saveBtn.addEventListener('click', async () => {
    const ids = getSelectedWarehouseIds();
    if (ids.length === 0) {
      setWhStatus('error', `${OTTO_ICON.info}Bitte mindestens ein Vario-Lager markieren.`);
      return;
    }
    const def = getDefaultWarehouse();
    saveBtn.disabled = true;
    setWhStatus('info', `${OTTO_ICON.info}Speichere Lager …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_storefront_warehouse', {
        _storefront: 'otto',
        _storage_ids: ids,
        _default_storage_id: def?.id ?? ids[0],
        _default_label: def?.label ?? null,
      });
      if (error) throw new Error(error.message);
      whList.style.borderColor = '';
      setWhStatus('ready', `${OTTO_ICON.check}${ids.length} Lager gespeichert${def ? ` (Standard „${ottoEscapeHtml(def.label)}")` : ''}`);
    } catch (err) {
      setWhStatus('error', `${OTTO_ICON.info}Speichern fehlgeschlagen: ${ottoEscapeHtml((err as Error).message)}`);
    } finally {
      saveBtn.disabled = false;
    }
  });

  whReload.addEventListener('click', () => void loadWarehouses(getSelectedWarehouseIds(), getDefaultWarehouse()?.id ?? null));

  // Standard-Preis-Verkaufskanal: Dropdown der Vario-Kanäle, Vorauswahl = gespeicherter Preis-Kanal
  // ODER (Default) der verknüpfte Otto-Kanal. Speichert on-change sofort (otto_set_price_channel);
  // keine Browser-Dialoge. `pcStorefront` merkt den Fallback-Kanal für die Default-Markierung.
  let pcStorefrontChannelId = '';
  function setPcStatus(kind: 'info' | 'ready' | 'error', html: string) {
    pcStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    pcStatus.innerHTML = html;
  }
  async function loadPriceChannel(row: StorefrontRow) {
    pcStorefrontChannelId = row.vario_channel_id || '';
    const selected = row.vario_price_channel_id || row.vario_channel_id || '';
    setPcStatus('info', `${OTTO_ICON.info}Kanäle werden geladen …`);
    const r = await fetchVarioChannels(true); // ALLE Vario-Kanäle (nicht nur der Otto-App-Kanal)
    if (r.authError) { setPcStatus('error', `${OTTO_ICON.info}Vario-Sitzung abgelaufen — App neu laden`); return; }
    if (!r.channels) { setPcStatus('error', `${OTTO_ICON.info}Kanalliste nicht ladbar`); return; }
    const opts = r.channels.map((c) => {
      const isDefault = c.id === pcStorefrontChannelId;
      const label = (c.label || c.id) + (isDefault ? ' (Standard)' : '');
      return `<option value="${ottoEscapeHtml(c.id)}"${c.id === selected ? ' selected' : ''}>${ottoEscapeHtml(label)}</option>`;
    });
    // Falls der gespeicherte Kanal nicht (mehr) in der Liste ist, trotzdem als Option zeigen.
    if (selected && !r.channels.some((c) => c.id === selected)) {
      opts.unshift(`<option value="${ottoEscapeHtml(selected)}" selected>${ottoEscapeHtml(row.vario_price_channel_label || selected)}</option>`);
    }
    pcSelect.innerHTML = opts.join('');
    const usesDefault = !row.vario_price_channel_id || row.vario_price_channel_id === pcStorefrontChannelId;
    setPcStatus('ready', usesDefault
      ? `${OTTO_ICON.check}Standard-Kanal (Otto-Verkaufskanal)`
      : `${OTTO_ICON.check}Eigener Preis-Kanal gewählt`);
  }
  pcSelect.addEventListener('change', async () => {
    const cid = pcSelect.value;
    const label = pcSelect.options[pcSelect.selectedIndex]?.textContent?.replace(/ \(Standard\)$/, '') ?? null;
    pcSelect.disabled = true;
    setPcStatus('info', `${OTTO_ICON.info}Speichere Preis-Kanal …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_price_channel', {
        _storefront: 'otto',
        _channel_id: cid,
        _channel_label: label,
      });
      if (error) throw new Error(error.message);
      const usesDefault = !cid || cid === pcStorefrontChannelId;
      setPcStatus('ready', usesDefault
        ? `${OTTO_ICON.check}Standard-Kanal (Otto-Verkaufskanal)`
        : `${OTTO_ICON.check}Preis-Kanal gespeichert`);
    } catch (err) {
      setPcStatus('error', `${OTTO_ICON.info}Speichern fehlgeschlagen: ${ottoEscapeHtml((err as Error).message)}`);
    } finally {
      pcSelect.disabled = false;
    }
  });
  pcReload.addEventListener('click', () => { if (currentRow) void loadPriceChannel(currentRow); });

  // Bestand-Auto-Push: Schalter aus der Angebotsübersicht hierher (Otto-Einstellungen) verschoben.
  // Liest den Wert (otto_get_auto_push_stock), persistiert Änderungen (otto_set_auto_push_stock);
  // revert bei Fehler, keine Browser-Dialoge. Listener EINMAL registriert, Wert einmal geladen.
  function setApStatus(kind: 'info' | 'ready' | 'error', html: string) {
    apStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    apStatus.innerHTML = html;
  }
  async function initAutoPushToggle() {
    try {
      const { data, error } = await ctx.supabase.rpc('otto_get_auto_push_stock', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      apToggle.checked = data === true;
      setApStatus('ready', apToggle.checked
        ? `${OTTO_ICON.check}Automatischer Bestand-Push aktiv`
        : `${OTTO_ICON.check}Bestand geht nur über „Aktualisieren“ an OTTO`);
    } catch (e) {
      setApStatus('error', `${OTTO_ICON.info}Status nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      apToggle.disabled = false;
    }
  }
  apToggle.addEventListener('change', async () => {
    const checked = apToggle.checked;
    apToggle.disabled = true;
    setApStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_auto_push_stock', { _storefront: 'otto', _enabled: checked });
      if (error) throw new Error(error.message);
      setApStatus('ready', checked
        ? `${OTTO_ICON.check}Automatischer Bestand-Push aktiviert`
        : `${OTTO_ICON.check}Automatischer Bestand-Push deaktiviert`);
      showToast(`Bestand-Auto-Push ${checked ? 'aktiviert' : 'deaktiviert'}`, 'success');
    } catch (e) {
      apToggle.checked = !checked; // Revert.
      setApStatus('error', `${OTTO_ICON.info}Konnte nicht ändern: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      apToggle.disabled = false;
    }
  });
  void initAutoPushToggle();

  // Preis-Auto-Push: eigener Schalter neben dem Bestand (Migration 0023), gleiches Muster.
  function setPpStatus(kind: 'info' | 'ready' | 'error', html: string) {
    ppStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    ppStatus.innerHTML = html;
  }
  async function initPricePushToggle() {
    try {
      const { data, error } = await ctx.supabase.rpc('otto_get_auto_push_prices', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      ppToggle.checked = data === true;
      setPpStatus('ready', ppToggle.checked
        ? `${OTTO_ICON.check}Automatischer Preis-Push aktiv`
        : `${OTTO_ICON.check}Preise gehen nur über „Aktualisieren“ oder die Angebotsliste an OTTO`);
    } catch (e) {
      setPpStatus('error', `${OTTO_ICON.info}Status nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      ppToggle.disabled = false;
    }
  }
  ppToggle.addEventListener('change', async () => {
    const checked = ppToggle.checked;
    ppToggle.disabled = true;
    setPpStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_auto_push_prices', { _storefront: 'otto', _enabled: checked });
      if (error) throw new Error(error.message);
      setPpStatus('ready', checked
        ? `${OTTO_ICON.check}Automatischer Preis-Push aktiviert`
        : `${OTTO_ICON.check}Automatischer Preis-Push deaktiviert`);
      showToast(`Preis-Auto-Push ${checked ? 'aktiviert' : 'deaktiviert'}`, 'success');
    } catch (e) {
      ppToggle.checked = !checked; // Revert.
      setPpStatus('error', `${OTTO_ICON.info}Konnte nicht ändern: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      ppToggle.disabled = false;
    }
  });
  void initPricePushToggle();

  // Kategorie bei der Zuordnung: otto_get/set_assign_category_mode (Migration 0014). Speichern
  // on-change, revert bei Fehler.
  const CM_LABEL: Record<string, string> = {
    take: 'Kategorie wird aus OTTO übernommen',
    skip: 'Kategorie wird nicht übernommen',
    ask: 'Bei jeder Zuordnung wird gefragt',
  };
  function setCmStatus(kind: 'info' | 'ready' | 'error', html: string) {
    cmStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    cmStatus.innerHTML = html;
  }
  let cmCurrent = 'ask';
  async function initCategoryMode() {
    try {
      const { data, error } = await ctx.supabase.rpc('otto_get_assign_category_mode', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      cmCurrent = typeof data === 'string' && data in CM_LABEL ? data : 'ask';
      cmSelect.value = cmCurrent;
      setCmStatus('ready', `${OTTO_ICON.check}${CM_LABEL[cmCurrent]}`);
    } catch (e) {
      setCmStatus('error', `${OTTO_ICON.info}Einstellung nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      cmSelect.disabled = false;
    }
  }
  cmSelect.addEventListener('change', async () => {
    const mode = cmSelect.value;
    cmSelect.disabled = true;
    setCmStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_assign_category_mode', { _storefront: 'otto', _mode: mode });
      if (error) throw new Error(error.message);
      cmCurrent = mode;
      setCmStatus('ready', `${OTTO_ICON.check}${CM_LABEL[mode] ?? ''}`);
      showToast('Einstellung gespeichert.', 'success');
    } catch (e) {
      cmSelect.value = cmCurrent; // Revert.
      setCmStatus('error', `${OTTO_ICON.info}Konnte nicht ändern: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      cmSelect.disabled = false;
    }
  });
  void initCategoryMode();

  // Marke bei der Zuordnung: otto_get/set_assign_brand_mode (Migration 0017). Gleiches Muster.
  const BM_LABEL: Record<string, string> = {
    take: 'Marke wird aus OTTO übernommen',
    skip: 'Marke wird nicht übernommen',
    ask: 'Bei jeder Zuordnung wird gefragt',
  };
  function setBmStatus(kind: 'info' | 'ready' | 'error', html: string) {
    bmStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    bmStatus.innerHTML = html;
  }
  let bmCurrent = 'ask';
  async function initBrandMode() {
    try {
      const { data, error } = await ctx.supabase.rpc('otto_get_assign_brand_mode', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      bmCurrent = typeof data === 'string' && data in BM_LABEL ? data : 'ask';
      bmSelect.value = bmCurrent;
      setBmStatus('ready', `${OTTO_ICON.check}${BM_LABEL[bmCurrent]}`);
    } catch (e) {
      setBmStatus('error', `${OTTO_ICON.info}Einstellung nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      bmSelect.disabled = false;
    }
  }
  bmSelect.addEventListener('change', async () => {
    const mode = bmSelect.value;
    bmSelect.disabled = true;
    setBmStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_assign_brand_mode', { _storefront: 'otto', _mode: mode });
      if (error) throw new Error(error.message);
      bmCurrent = mode;
      setBmStatus('ready', `${OTTO_ICON.check}${BM_LABEL[mode] ?? ''}`);
      showToast('Einstellung gespeichert.', 'success');
    } catch (e) {
      bmSelect.value = bmCurrent; // Revert.
      setBmStatus('error', `${OTTO_ICON.info}Konnte nicht ändern: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      bmSelect.disabled = false;
    }
  });
  void initBrandMode();

  // --- Auftragsübernahme: Vario-Pflichtmodalitäten + Auto-Übernahme-Toggle. --------------------
  // Optionen aus vario-payment-options-list (Otto-Kontext); Speichern on-change
  // (otto_set_order_takeover_config, alle vier zusammen). Auto-Toggle separat (otto_set_auto_takeover).
  // Aktuelle Werte + Toggle-Zustand stammen aus currentRow (otto_list_storefronts).
  interface OptItem { id: string; label: string }
  function setOtStatus(kind: 'info' | 'ready' | 'error', html: string) {
    otStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    otStatus.innerHTML = html;
  }
  function setAtStatus(kind: 'info' | 'ready' | 'error', html: string) {
    atStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    atStatus.innerHTML = html;
  }
  function fillOtSelect(sel: HTMLSelectElement, items: OptItem[], selectedId: string | null, selectedLabel: string | null) {
    const opts = ['<option value="">(nicht gesetzt)</option>'];
    let found = false;
    for (const it of items) {
      const isSel = it.id === selectedId;
      if (isSel) found = true;
      opts.push(`<option value="${ottoEscapeHtml(it.id)}"${isSel ? ' selected' : ''}>${ottoEscapeHtml(it.label || it.id)}</option>`);
    }
    if (selectedId && !found) {
      opts.splice(1, 0, `<option value="${ottoEscapeHtml(selectedId)}" selected>${ottoEscapeHtml(selectedLabel || selectedId)}</option>`);
    }
    sel.innerHTML = opts.join('');
  }
  function otComplete(): boolean { return !!(otPm.value && otPt.value && otDm.value && otDt.value); }
  function refreshOtStatus() {
    setOtStatus(otComplete() ? 'ready' : 'info', otComplete()
      ? `${OTTO_ICON.check}Vollständig: Bestellungen können übernommen werden`
      : `${OTTO_ICON.info}Bitte alle vier Felder setzen, damit die Übernahme möglich ist`);
  }
  async function loadOrderTakeover(row: StorefrontRow) {
    setOtStatus('info', `${OTTO_ICON.info}Vario-Optionen werden geladen …`);
    try {
      const res = await fetch(`${functionsBase}/functions/v1/vario-payment-options-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ marketplace: 'otto' }),
      });
      const j = await res.json() as {
        ok?: boolean; methods?: OptItem[]; terms?: OptItem[];
        deliveryMethods?: OptItem[]; deliveryTerms?: OptItem[]; error?: { message?: string };
      };
      if (!res.ok || j.error) throw new Error(j.error?.message ?? httpErrorText(res.status));
      fillOtSelect(otPm, j.methods ?? [], row.vario_payment_method_id ?? null, row.vario_payment_method_label ?? null);
      fillOtSelect(otPt, j.terms ?? [], row.vario_payment_term_id ?? null, row.vario_payment_term_label ?? null);
      fillOtSelect(otDm, j.deliveryMethods ?? [], row.vario_delivery_method_id ?? null, row.vario_delivery_method_label ?? null);
      fillOtSelect(otDt, j.deliveryTerms ?? [], row.vario_delivery_term_id ?? null, row.vario_delivery_term_label ?? null);
      otPm.disabled = otPt.disabled = otDm.disabled = otDt.disabled = false;
      refreshOtStatus();
    } catch (e) {
      setOtStatus('error', `${OTTO_ICON.info}Optionen nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    }
    atToggle.checked = row.auto_takeover_orders === true;
    atToggle.disabled = false;
    setAtStatus('ready', atToggle.checked
      ? `${OTTO_ICON.check}Automatische Übernahme aktiv`
      : `${OTTO_ICON.check}Übernahme nur manuell im Bestelldetail`);
  }
  function otLabelOf(sel: HTMLSelectElement): string | null {
    const o = sel.options[sel.selectedIndex];
    return o && o.value ? (o.textContent ?? null) : null;
  }
  async function saveOrderTakeover() {
    otPm.disabled = otPt.disabled = otDm.disabled = otDt.disabled = true;
    setOtStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_order_takeover_config', {
        _storefront: 'otto',
        _payment_method_id: otPm.value || null,  _payment_method_label: otLabelOf(otPm),
        _payment_term_id: otPt.value || null,    _payment_term_label: otLabelOf(otPt),
        _delivery_method_id: otDm.value || null, _delivery_method_label: otLabelOf(otDm),
        _delivery_term_id: otDt.value || null,   _delivery_term_label: otLabelOf(otDt),
      });
      if (error) throw new Error(error.message);
      refreshOtStatus();
    } catch (e) {
      setOtStatus('error', `${OTTO_ICON.info}Speichern fehlgeschlagen: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      otPm.disabled = otPt.disabled = otDm.disabled = otDt.disabled = false;
    }
  }
  [otPm, otPt, otDm, otDt].forEach((s) => s.addEventListener('change', () => void saveOrderTakeover()));
  otReload.addEventListener('click', () => { if (currentRow) void loadOrderTakeover(currentRow); });
  atToggle.addEventListener('change', async () => {
    const checked = atToggle.checked;
    atToggle.disabled = true;
    setAtStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_auto_takeover', { _storefront: 'otto', _enabled: checked });
      if (error) throw new Error(error.message);
      setAtStatus('ready', checked
        ? `${OTTO_ICON.check}Automatische Übernahme aktiviert`
        : `${OTTO_ICON.check}Übernahme nur manuell im Bestelldetail`);
      showToast(`Automatische Auftragsübernahme ${checked ? 'aktiviert' : 'deaktiviert'}`, 'success');
    } catch (e) {
      atToggle.checked = !checked;
      setAtStatus('error', `${OTTO_ICON.info}Konnte nicht ändern: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      atToggle.disabled = false;
    }
  });

  // --- Versand: Absenderadresse (otto_get_ship_from / otto_set_ship_from, Migration 0009). -------
  // Speichern beim Verlassen eines Feldes, alle drei zusammen. Unvollständig = Hinweis, kein Fehler.
  let sfSaved = '';
  function setSfStatus(kind: 'info' | 'ready' | 'error', html: string) {
    sfStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    sfStatus.innerHTML = html;
  }
  function sfValue(): string { return [sfZip.value, sfCity.value, sfCountry.value].join('|'); }
  // Normalisiert die drei Felder sichtbar im Formular, damit gespeichert = angezeigt.
  function normalizeSfFields() {
    sfCountry.value = normalizeText(sfCountry.value).replace(/ /g, '').toUpperCase();
    sfZip.value = normalizeZip(sfZip.value, sfCountry.value);
    sfCity.value = normalizeText(sfCity.value);
  }
  function refreshSfStatus() {
    const complete = !!(sfZip.value && sfCity.value && sfCountry.value);
    setSfStatus(complete ? 'ready' : 'info', complete
      ? `${OTTO_ICON.check}Absenderadresse gesetzt`
      : `${OTTO_ICON.info}Bitte PLZ, Ort und Land setzen, sonst lehnt OTTO manuelle Versandmeldungen ab`);
  }
  async function loadShipFrom() {
    try {
      const { data, error } = await ctx.supabase.rpc('otto_get_ship_from', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      const r = (Array.isArray(data) ? data[0] : data) as { zip_code?: string | null; city?: string | null; country_code?: string | null } | null;
      sfZip.value = r?.zip_code ?? '';
      sfCity.value = r?.city ?? '';
      sfCountry.value = r?.country_code ?? '';
      sfSaved = sfValue();
      normalizeSfFields();
      refreshSfStatus();
    } catch (e) {
      setSfStatus('error', `${OTTO_ICON.info}Absenderadresse nicht ladbar: ${ottoEscapeHtml((e as Error).message)}`);
    } finally {
      sfZip.disabled = sfCity.disabled = sfCountry.disabled = false;
    }
  }
  async function saveShipFrom() {
    normalizeSfFields();
    // Unverändert gegenüber dem Gespeicherten: nichts speichern, aber einen alten Fehlerhinweis
    // (z. B. nach „DE“ und zurück auf „DEU“) durch den aktuellen Stand ersetzen.
    if (sfValue() === sfSaved) { refreshSfStatus(); return; }
    const country = sfCountry.value;
    if (country && !/^[A-Z]{3}$/.test(country)) {
      setSfStatus('error', `${OTTO_ICON.info}Land bitte als dreistelligen Code angeben, z. B. DEU`);
      return;
    }
    setSfStatus('info', `${OTTO_ICON.info}Speichere …`);
    try {
      const { error } = await ctx.supabase.rpc('otto_set_ship_from', {
        _storefront: 'otto',
        _zip_code: sfZip.value || null,
        _city: sfCity.value || null,
        _country_code: country || null,
      });
      if (error) throw new Error(error.message);
      sfSaved = sfValue();
      refreshSfStatus();
    } catch (e) {
      setSfStatus('error', `${OTTO_ICON.info}Speichern fehlgeschlagen: ${ottoEscapeHtml((e as Error).message)}`);
    }
  }
  [sfZip, sfCity, sfCountry].forEach((el) => el.addEventListener('blur', () => void saveShipFrom()));

  // Kategorie-Sync: enqueue (otto_enqueue_category_push) + Polling (otto_get_active_category_jobs).
  // Inline-Status, KEINE Browser-Dialoge. Liegt im #sc-form (Aktiv-Zustand) → nur sichtbar,
  // wenn ein Verkaufskanal existiert (Voraussetzung fuer den Kategorie-Push).
  const catBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#cat-select-btn');
  const catStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#cat-sync-status');
  let catPoll: ReturnType<typeof setInterval> | undefined;

  function setCatStatus(kind: 'info' | 'ready' | 'error', html: string) {
    if (!catStatus) return;
    catStatus.className = kind === 'info' ? 'foot-status' : `foot-status ${kind}`;
    catStatus.innerHTML = html;
  }
  function startCatPolling() { if (!catPoll) catPoll = setInterval(() => void pollCatJobs(), 5_000); }
  function stopCatPolling() { if (catPoll) { clearInterval(catPoll); catPoll = undefined; } }

  // Lebenszeichen: formatiert den Fortschritt aus account.jobs.result (vom Worker live geschrieben).
  function fmtCatProgress(result: Record<string, unknown> | null | undefined): string {
    const r = (result ?? {}) as Record<string, number | string>;
    const stampRaw = typeof r.updatedAt === 'string' ? r.updatedAt : '';
    let stamp = '';
    try { stamp = stampRaw ? ` · Stand ${new Date(stampRaw).toLocaleTimeString('de-DE')}` : ''; } catch { stamp = ''; }
    if (r.phase === 'refresh') return `Lade OTTO-Katalog … (${r.refreshedGroups ?? 0} Gruppen)${stamp}`;
    if (r.phase === 'push') {
      const kept = Number(r.keptWithArticles ?? 0);
      const keptTxt = kept > 0 ? `, ${kept} behalten (Artikel)` : '';
      return `Übernehme nach Vario … (Gruppen +${r.groupsPushed ?? 0}/-${r.groupsDeleted ?? 0}, Kategorien +${r.leavesPushed ?? 0}/-${r.leavesDeleted ?? 0}${keptTxt})${stamp}`;
    }
    return `Synchronisiere Kategorien …${stamp}`;
  }

  // Self-managing: bei laufendem Job Button sperren + Polling sicherstellen + Fortschritt zeigen;
  // bei completed/failed Polling stoppen + Button freigeben. Wird auch beim Render (Resume) gerufen,
  // damit ein bereits laufender Sync nach Fenster-Wechsel sichtbar bleibt.
  async function pollCatJobs() {
    const { data } = await ctx.supabase.rpc('otto_get_active_category_jobs');
    const latest = (data as Array<{ status: string; result: Record<string, unknown> | null }> | null)?.[0];
    if (!latest) return;
    if (latest.status === 'completed') {
      stopCatPolling();
      if (catBtn) catBtn.disabled = false;
      const r = (latest.result ?? {}) as Record<string, number>;
      setCatStatus('ready', `${OTTO_ICON.check}Kategorien synchronisiert (${r.groupsPushed ?? 0} angelegt, ${r.groupsDeleted ?? 0} entfernt)`);
    } else if (latest.status === 'failed' || latest.status === 'cancelled') {
      stopCatPolling();
      if (catBtn) catBtn.disabled = false;
      setCatStatus('error', `${OTTO_ICON.info}Sync ${latest.status === 'cancelled' ? 'abgebrochen' : 'fehlgeschlagen'} — bitte erneut versuchen`);
    } else {
      if (catBtn) catBtn.disabled = true;
      startCatPolling();
      setCatStatus('info', `<span class="spin"></span>${fmtCatProgress(latest.result)}`);
    }
  }
  // Auswahl-Dialog: Gruppen (otto_list_category_groups) mit gespeicherter Auswahl
  // (otto_get_selected_category_groups) → Speichern (otto_set_selected_category_groups)
  // + Reconcile-Enqueue (otto_enqueue_category_push). Fehler INLINE im Modal (#otto-cat-error),
  // Erfolg via showToast — KEINE Browser-Dialoge. Polling/Button-Sperre wie bisher.
  async function openCategoryPicker() {
    const body = `
      <p class="hint" style="margin:0 0 10px;">Ausgewählt werden <b>Kategoriegruppen</b>, zum Beispiel „Postkarten“. Jede Gruppe bringt alle ihre Kategorien mit, etwa „Grußkarten“ und „Klappkarte“. Die Suche findet beides: Wer nach einer Kategorie sucht, bekommt die Gruppe angezeigt, in der sie steckt.</p>
      <div class="kl-form-group" style="display:flex;gap:8px;align-items:center;">
        <div class="kl-search" style="flex:1;">
          <input type="text" id="otto-cat-search" class="inp" placeholder="Gruppe oder Kategorie suchen …" autocomplete="off">
        </div>
        <button type="button" id="otto-cat-sort" class="btn btn-ghost" title="Nach Name sortieren">Name ↓</button>
        <label style="display:flex;align-items:center;gap:6px;white-space:nowrap;font-size:13px;">
          <input type="checkbox" id="otto-cat-only-sel"> nur Ausgewählte
        </label>
      </div>
      <div id="otto-cat-hint" class="foot-status" style="margin:6px 0;"></div>
      <div id="otto-cat-list" style="flex:0 1 320px;min-height:120px;overflow:auto;margin-top:4px;">
        <div class="foot-status">Lade Kategoriegruppen …</div>
      </div>
      <div id="otto-cat-foot" style="display:flex;align-items:center;gap:6px;justify-content:center;margin-top:10px;">
        <button type="button" class="btn btn-ghost" id="oc-first" title="Erste Seite">«</button>
        <button type="button" class="btn btn-ghost" id="oc-prev" title="Vorherige Seite">‹</button>
        <span id="oc-pginfo" style="min-width:180px;text-align:center;">–</span>
        <button type="button" class="btn btn-ghost" id="oc-next" title="Nächste Seite">›</button>
        <button type="button" class="btn btn-ghost" id="oc-last" title="Letzte Seite">»</button>
      </div>
      <div class="kl-modal-error" id="otto-cat-error" hidden></div>`;
    const modal = openModal({
      title: 'OTTO-Kategoriegruppen auswählen',
      icon: 'fa-sitemap',
      width: 640,
      body,
      footer: `<button type="button" class="v-button v-button--primary" data-action="save">
                 <span class="v-icon fa-light fa-check"></span> Speichern &amp; übernehmen (<span id="otto-cat-count">0</span> Gruppen)
               </button>
               <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>`,
      // Fest auf Höhe des Knopfs und Liste mit fester Höhe: Beim Filtern ändert sich die
      // Trefferzahl, eine zentrierte Box würde sonst bei jedem Tastendruck springen.
      onMount: (root) => pinModalToOpener(root),
    });

    const listEl = modal.el.querySelector<HTMLDivElement>('#otto-cat-list')!;
    const searchEl = modal.el.querySelector<HTMLInputElement>('#otto-cat-search')!;
    const sortBtn = modal.el.querySelector<HTMLButtonElement>('#otto-cat-sort')!;
    const onlySelEl = modal.el.querySelector<HTMLInputElement>('#otto-cat-only-sel')!;
    const hintEl = modal.el.querySelector<HTMLDivElement>('#otto-cat-hint')!;
    const countEl = modal.el.querySelector<HTMLSpanElement>('#otto-cat-count')!;
    const errEl = modal.el.querySelector<HTMLDivElement>('#otto-cat-error')!;
    const saveBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="save"]')!;
    const pgInfo = modal.el.querySelector<HTMLSpanElement>('#oc-pginfo')!;
    const btnFirst = modal.el.querySelector<HTMLButtonElement>('#oc-first')!;
    const btnPrev = modal.el.querySelector<HTMLButtonElement>('#oc-prev')!;
    const btnNext = modal.el.querySelector<HTMLButtonElement>('#oc-next')!;
    const btnLast = modal.el.querySelector<HTMLButtonElement>('#oc-last')!;
    const footEl = modal.el.querySelector<HTMLDivElement>('#otto-cat-foot')!;

    const checked = new Set<string>();
    let sortAsc = true;
    let page = 0;
    const PAGE_SIZE = 50;
    const updateCount = () => { countEl.textContent = String(checked.size); };

    // Bereits gewählte Gruppen laden (nur Namen — Auswahl-Einheit ist die Gruppe).
    {
      const { data: sel, error: se } = await ctx.supabase.rpc('otto_get_selected_category_groups');
      if (se) { errEl.hidden = false; errEl.textContent = `Auswahl nicht ladbar: ${se.message}`; }
      for (const row of (sel as Array<{ category_group: string }> | null) ?? []) checked.add(row.category_group);
    }
    updateCount();

    // Treffer nur in den Kategorien der Gruppe (nicht im Gruppennamen): bis zu drei davon nennen,
    // damit klar ist, warum die Gruppe erscheint (Suche „Grußkarten" → Gruppe „Postkarten").
    const matchedLeaves = (name: string, leaves: string[] | null, term: string): string[] => {
      const f = term.toLowerCase();
      if (!f || name.toLowerCase().includes(f)) return [];
      return (leaves ?? []).filter((c) => c.toLowerCase().includes(f));
    };
    const rowHtml = (name: string, leafCount: number | null, matched: string[] = []) =>
      `<label class="kl-check-row" style="display:flex;align-items:center;gap:10px;padding:6px 2px;cursor:pointer;">
         <input type="checkbox" data-group="${ottoEscapeHtml(name)}"${checked.has(name) ? ' checked' : ''}>
         <span style="flex:1;">${ottoEscapeHtml(name)}${matched.length
           ? `<span class="hint" style="display:block;">enthält: ${ottoEscapeHtml(matched.slice(0, 3).join(', '))}${matched.length > 3 ? ` und ${matched.length - 3} weitere` : ''}</span>`
           : ''}</span>
         ${leafCount !== null ? `<span class="hint">${leafCount} Kat.</span>` : ''}
       </label>`;
    // Suche in Gruppenname UND Kategorien (View otto.category_groups_search, Migration 0012).
    // Wert in doppelten Anführungszeichen, damit Komma, Klammern und Punkt im Begriff den
    // PostgREST-or-Filter nicht zerlegen.
    const termFilter = (term: string) => {
      const v = `"%${term.replace(/[\\"]/g, (c) => `\\${c}`)}%"`;
      return `category_group.ilike.${v},categories_text.ilike.${v}`;
    };
    const wireRows = () => {
      listEl.querySelectorAll<HTMLInputElement>('input[data-group]').forEach((cb) =>
        cb.addEventListener('change', () => {
          const grp = cb.dataset['group']!;
          if (cb.checked) checked.add(grp); else checked.delete(grp);
          updateCount();
        }));
    };

    // Serverseitige Paginierung über otto.category_groups (1945 Gruppen → der 1000er-Cap der
    // RPC würde clientseitig kappen). Filter/Sort/Range exakt wie der Marken-Picker.
    const renderPage = async () => {
      const term = searchEl.value.trim();
      // „Nur Ausgewählte": aus der lokalen Auswahl, ohne Server-Pager.
      if (onlySelEl.checked) {
        footEl.style.display = 'none';
        const f = term.toLowerCase();
        const names = [...checked]
          .filter((n) => !f || n.toLowerCase().includes(f))
          .sort((a, b) => sortAsc ? a.localeCompare(b) : b.localeCompare(a));
        hintEl.textContent = `${names.length} ausgewählte Gruppe${names.length === 1 ? '' : 'n'}`;
        listEl.innerHTML = names.length ? names.map((n) => rowHtml(n, null)).join('') : `<div class="foot-status">Keine ausgewählten Gruppen.</div>`;
        wireRows();
        return;
      }
      footEl.style.display = 'flex';
      listEl.innerHTML = `<div class="foot-status">Lade Kategoriegruppen …</div>`;
      let q = ctx.supabase.schema('otto').from('category_groups_search').select('category_group, categories', { count: 'exact' });
      if (term.length >= 1) q = q.or(termFilter(term)); // enthält, in Gruppe oder Kategorie
      const from = page * PAGE_SIZE;
      const { data, error, count } = await q.order('category_group', { ascending: sortAsc }).range(from, from + PAGE_SIZE - 1);
      if (error) { listEl.innerHTML = `<div class="foot-status">Fehler: ${ottoEscapeHtml(error.message)}</div>`; return; }
      const rows = (data ?? []) as Array<{ category_group: string; categories: string[] | null }>;
      const total = count ?? 0;
      const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      pgInfo.textContent = `${total === 0 ? 0 : from + 1}–${Math.min(total, from + PAGE_SIZE)} von ${total.toLocaleString('de-DE')} · Seite ${page + 1}/${pages}`;
      btnFirst.disabled = page === 0; btnPrev.disabled = page === 0;
      btnNext.disabled = page + 1 >= pages; btnLast.disabled = page + 1 >= pages;
      hintEl.textContent = (term ? `${total.toLocaleString('de-DE')} Kategoriegruppe${total === 1 ? '' : 'n'} zu „${term}"` : `${total.toLocaleString('de-DE')} Kategoriegruppen`) + ` · ${checked.size} ausgewählt`;
      listEl.innerHTML = rows.length ? rows.map((r) => rowHtml(r.category_group, (r.categories ?? []).length, matchedLeaves(r.category_group, r.categories, term))).join('') : `<div class="foot-status">Keine Treffer.</div>`;
      wireRows();
    };
    const goto = (p: number) => { page = Math.max(0, p); void renderPage(); };

    let timer: number | null = null;
    searchEl.addEventListener('input', () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => { page = 0; void renderPage(); }, 200) as unknown as number;
    });
    sortBtn.addEventListener('click', () => {
      sortAsc = !sortAsc;
      sortBtn.textContent = sortAsc ? 'Name ↓' : 'Name ↑';
      page = 0; void renderPage();
    });
    onlySelEl.addEventListener('change', () => { page = 0; void renderPage(); });
    btnFirst.addEventListener('click', () => goto(0));
    btnPrev.addEventListener('click', () => goto(page - 1));
    btnNext.addEventListener('click', () => goto(page + 1));
    btnLast.addEventListener('click', async () => {
      const term = searchEl.value.trim();
      let q = ctx.supabase.schema('otto').from('category_groups_search').select('category_group', { count: 'exact', head: true });
      if (term.length >= 1) q = q.or(termFilter(term));
      const { count } = await q;
      goto(Math.max(0, Math.ceil((count ?? 0) / PAGE_SIZE) - 1));
    });
    void renderPage();

    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true;
      errEl.hidden = true;
      try {
        const selectedGroups = [...checked];
        const { error: setErr } = await ctx.supabase.rpc('otto_set_selected_category_groups', { p_groups: selectedGroups });
        if (setErr) throw new Error(setErr.message);
        const { data: enqData, error: enqErr } = await ctx.supabase.rpc('otto_enqueue_category_push');
        if (enqErr) throw new Error(enqErr.message);
        const enq = (Array.isArray(enqData) ? enqData[0] : enqData) as { reason?: string | null } | null;
        modal.close();
        showToast(enq?.reason === 'running'
          ? `Auswahl gespeichert (${selectedGroups.length} Gruppen). Ein Sync läuft bereits — Fortschritt wird angezeigt.`
          : `Auswahl gespeichert (${selectedGroups.length} Gruppen) — Übernahme läuft …`);
        startCatPolling();
        void pollCatJobs();
      } catch (err) {
        saveBtn.disabled = false;
        errEl.hidden = false;
        errEl.textContent = (err as Error).message;
      }
    });
  }

  catBtn?.addEventListener('click', () => void openCategoryPicker());

  // ── Präferierte Marken ──────────────────────────────────────────────────
  const brandBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#brand-select-btn');
  const brandStatus = ctx.contentEl.querySelector<HTMLSpanElement>('#brand-sel-status');
  function setBrandStatus(html: string) { if (brandStatus) brandStatus.innerHTML = html; }
  async function refreshBrandCount() {
    const { data } = await ctx.supabase.rpc('otto_list_selected_brands');
    const n = (data as unknown[] | null)?.length ?? 0;
    setBrandStatus(n > 0 ? `${OTTO_ICON.check}${n} präferierte Marke${n === 1 ? '' : 'n'}` : `${OTTO_ICON.info}Noch keine Marken gewählt`);
  }
  void refreshBrandCount();
  brandBtn?.addEventListener('click', () => void openBrandPicker());

  async function openBrandPicker() {
    const body = `
      <div class="kl-form-group" style="display:flex;gap:8px;align-items:center;">
        <div class="kl-search" style="flex:1;">
          <input type="text" id="otto-brand-search" class="inp" placeholder="Marke filtern …" autocomplete="off">
        </div>
        <button type="button" id="otto-brand-sort" class="btn btn-ghost" title="Nach Name sortieren">Name ↓</button>
        <label style="display:flex;align-items:center;gap:6px;white-space:nowrap;font-size:13px;">
          <input type="checkbox" id="otto-brand-only-sel"> nur Ausgewählte
        </label>
      </div>
      <div id="otto-brand-hint" class="foot-status" style="margin:6px 0;"></div>
      <div id="otto-brand-list" style="flex:0 1 320px;min-height:120px;overflow:auto;margin-top:4px;">
        <div class="foot-status">Lade Marken …</div>
      </div>
      <div id="otto-brand-foot" style="display:flex;align-items:center;gap:6px;justify-content:center;margin-top:10px;">
        <button type="button" class="btn btn-ghost" id="ob-first" title="Erste Seite">«</button>
        <button type="button" class="btn btn-ghost" id="ob-prev" title="Vorherige Seite">‹</button>
        <span id="ob-pginfo" style="min-width:180px;text-align:center;">–</span>
        <button type="button" class="btn btn-ghost" id="ob-next" title="Nächste Seite">›</button>
        <button type="button" class="btn btn-ghost" id="ob-last" title="Letzte Seite">»</button>
      </div>
      <div class="kl-modal-error" id="otto-brand-error" hidden></div>`;
    const modal = openModal({
      title: 'Präferierte Marken auswählen',
      icon: 'fa-tag',
      width: 640,
      body,
      footer: `<button type="button" class="v-button v-button--primary" data-action="save"><span class="v-icon fa-light fa-check"></span> Speichern (<span id="otto-brand-count">0</span>)</button>
               <button type="button" class="v-button" data-modal-cancel>Abbrechen</button>`,
      // Wie die Kategoriegruppen: fest am Knopf und auf den sichtbaren Ausschnitt begrenzt.
      onMount: (root) => pinModalToOpener(root),
    });
    const searchEl = modal.el.querySelector<HTMLInputElement>('#otto-brand-search')!;
    const sortBtn = modal.el.querySelector<HTMLButtonElement>('#otto-brand-sort')!;
    const onlySelEl = modal.el.querySelector<HTMLInputElement>('#otto-brand-only-sel')!;
    const hintEl = modal.el.querySelector<HTMLDivElement>('#otto-brand-hint')!;
    const listEl = modal.el.querySelector<HTMLDivElement>('#otto-brand-list')!;
    const countEl = modal.el.querySelector<HTMLSpanElement>('#otto-brand-count')!;
    const errEl = modal.el.querySelector<HTMLDivElement>('#otto-brand-error')!;
    const saveBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="save"]')!;
    const footEl = modal.el.querySelector<HTMLDivElement>('#otto-brand-foot')!;
    const pgInfo = modal.el.querySelector<HTMLSpanElement>('#ob-pginfo')!;
    const btnFirst = modal.el.querySelector<HTMLButtonElement>('#ob-first')!;
    const btnPrev = modal.el.querySelector<HTMLButtonElement>('#ob-prev')!;
    const btnNext = modal.el.querySelector<HTMLButtonElement>('#ob-next')!;
    const btnLast = modal.el.querySelector<HTMLButtonElement>('#ob-last')!;

    // Markierte Marken (id → name) — bleiben über Seiten/Filter/Sort erhalten.
    const selected = new Map<string, string>();
    const { data: cur } = await ctx.supabase.rpc('otto_list_selected_brands');
    for (const b of (cur as Array<{ id: string; name: string }> | null) ?? []) selected.set(b.id, b.name);

    let sortAsc = true;
    let page = 0;
    const PAGE_SIZE = 50;
    const updateCount = () => { countEl.textContent = String(selected.size); };
    updateCount();

    const rowHtml = (id: string, name: string) =>
      `<label class="kl-check-row" style="display:flex;align-items:center;gap:10px;padding:6px 2px;cursor:pointer;">
         <input type="checkbox" data-brand-id="${ottoEscapeHtml(id)}" data-brand-name="${ottoEscapeHtml(name)}"${selected.has(id) ? ' checked' : ''}>
         <span style="flex:1;">${ottoEscapeHtml(name)}</span>
       </label>`;
    const wireRows = () => {
      listEl.querySelectorAll<HTMLInputElement>('input[data-brand-id]').forEach((cb) =>
        cb.addEventListener('change', () => {
          const id = cb.dataset['brandId']!;
          if (cb.checked) selected.set(id, cb.dataset['brandName']!); else selected.delete(id);
          updateCount();
        }));
    };

    const renderPage = async () => {
      const term = searchEl.value.trim();
      // „Nur Ausgewählte": rein aus der lokalen Map, ohne Server-Pager.
      if (onlySelEl.checked) {
        footEl.style.display = 'none';
        const f = term.toLowerCase();
        const rows = [...selected.entries()]
          .filter(([, n]) => !f || n.toLowerCase().includes(f))
          .sort((a, b) => sortAsc ? a[1].localeCompare(b[1]) : b[1].localeCompare(a[1]));
        hintEl.textContent = `${rows.length} ausgewählte Marke${rows.length === 1 ? '' : 'n'}`;
        listEl.innerHTML = rows.length ? rows.map(([id, n]) => rowHtml(id, n)).join('') : `<div class="foot-status">Keine ausgewählten Marken.</div>`;
        wireRows();
        return;
      }
      footEl.style.display = 'flex';
      listEl.innerHTML = `<div class="foot-status">Lade Marken …</div>`;
      let q = ctx.supabase.schema('otto').from('brands').select('id,name', { count: 'exact' }).eq('usable', true);
      if (term.length >= 1) q = q.ilike('name', `%${term}%`); // Volltext (enthält), nicht nur Präfix
      const from = page * PAGE_SIZE;
      const { data, error, count } = await q.order('name', { ascending: sortAsc }).range(from, from + PAGE_SIZE - 1);
      if (error) { listEl.innerHTML = `<div class="foot-status">Fehler: ${ottoEscapeHtml(error.message)}</div>`; return; }
      const rows = (data ?? []) as Array<{ id: string; name: string }>;
      const total = count ?? 0;
      const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
      pgInfo.textContent = `${total === 0 ? 0 : from + 1}–${Math.min(total, from + PAGE_SIZE)} von ${total.toLocaleString('de-DE')} · Seite ${page + 1}/${pages}`;
      btnFirst.disabled = page === 0; btnPrev.disabled = page === 0;
      btnNext.disabled = page + 1 >= pages; btnLast.disabled = page + 1 >= pages;
      hintEl.textContent = term ? `${total.toLocaleString('de-DE')} Kategoriegruppe${total === 1 ? '' : 'n'} zu „${term}"` : `${total.toLocaleString('de-DE')} verwendbare Marken — blättern oder filtern · markierte bleiben erhalten`;
      listEl.innerHTML = rows.length ? rows.map((b) => rowHtml(b.id, b.name)).join('') : `<div class="foot-status">Keine Treffer.</div>`;
      wireRows();
    };
    const goto = (p: number) => { page = Math.max(0, p); void renderPage(); };

    let timer: number | null = null;
    searchEl.addEventListener('input', () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => { page = 0; void renderPage(); }, 200) as unknown as number;
    });
    sortBtn.addEventListener('click', () => {
      sortAsc = !sortAsc;
      sortBtn.textContent = sortAsc ? 'Name ↓' : 'Name ↑';
      page = 0; void renderPage();
    });
    onlySelEl.addEventListener('change', () => { page = 0; void renderPage(); });
    btnFirst.addEventListener('click', () => goto(0));
    btnPrev.addEventListener('click', () => goto(page - 1));
    btnNext.addEventListener('click', () => goto(page + 1));
    btnLast.addEventListener('click', async () => {
      // letzte Seite: aus aktuellem Filter-Total berechnen
      const term = searchEl.value.trim();
      let q = ctx.supabase.schema('otto').from('brands').select('id', { count: 'exact', head: true }).eq('usable', true);
      if (term.length >= 1) q = q.ilike('name', `%${term}%`);
      const { count } = await q;
      goto(Math.max(0, Math.ceil((count ?? 0) / PAGE_SIZE) - 1));
    });

    void renderPage();

    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true; errEl.hidden = true;
      const { error } = await ctx.supabase.rpc('otto_set_selected_brands', { p_brand_ids: [...selected.keys()] });
      if (error) { errEl.hidden = false; errEl.textContent = `Speichern fehlgeschlagen: ${error.message}`; saveBtn.disabled = false; return; }
      modal.close();
      showToast(`${selected.size} präferierte Marke${selected.size === 1 ? '' : 'n'} gespeichert.`, 'success');
      void refreshBrandCount();
    });
  }

  removeBtn.addEventListener('click', async () => {
    if (!removeArmed) {
      removeArmed = true;
      removeBtn.classList.add('armed');
      removeBtn.innerHTML = `${OTTO_ICON.trash}Wirklich lösen?`;
      removeTimer = setTimeout(resetRemoveBtn, 3000);
      return;
    }
    resetRemoveBtn();
    removeBtn.disabled = true;
    try {
      const { error } = await ctx.supabase.rpc('otto_remove_storefront', { _storefront: 'otto' });
      if (error) throw new Error(error.message);
      removeBtn.disabled = false;
      renderEmpty();
    } catch (err) {
      removeBtn.disabled = false;
      setFoot('error', `${OTTO_ICON.info}Entfernen fehlgeschlagen: ${ottoEscapeHtml((err as Error).message)}`);
    }
  });

  btn.addEventListener('click', async () => {
    btn.disabled = true;
    btn.innerHTML = `<span class="spin"></span>Lege Verkaufskanal an …`;
    setFoot('info', `${OTTO_ICON.info}Verkaufskanal wird in Vario angelegt …`);
    const url = `${functionsBase}/functions/v1/otto-sales-channel-add`;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({}),
      });
      const payload = (await res.json().catch(() => ({}))) as AddResponse;
      btn.disabled = false;
      btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>Verkaufskanal anlegen`;
      if (!res.ok || !payload.ok) {
        const code = payload.error?.code;
        const msg =
          code === 'NO_OFFLINE_TOKEN'
            ? 'Vario-Token fehlt — bitte die Otto-App in Vario einmal deinstallieren und neu installieren.'
            : code === 'OTTO_UNAVAILABLE'
              ? 'Otto Market ist derzeit nicht erreichbar. Bitte später erneut versuchen.'
              : `${payload.error?.message ?? httpErrorText(res.status)}${code ? ` (${code})` : ''}`;
        setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml(msg)}`);
        return;
      }
      renderActive({
        storefront: 'otto',
        vario_channel_id: payload.channelId ?? '',
        vario_channel_label: payload.label ?? 'Otto',
        vario_storage_id: null,
        vario_storage_label: null,
        vario_storage_ids: null,
      });
    } catch (err) {
      btn.disabled = false;
      btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>Verkaufskanal anlegen`;
      setFoot('error', `${OTTO_ICON.info}Netzwerkfehler: ${ottoEscapeHtml((err as Error).message)}`);
    }
  });

  // Initial-Load zuletzt (alle Listener sind registriert).
  try {
    const { data, error } = await ctx.supabase.rpc('otto_list_storefronts');
    if (error) throw new Error(error.message);
    const rows = (data as StorefrontRow[] | null) ?? [];
    const row = rows.find((r) => r.storefront === 'otto');
    if (row) {
      // Entry-Health-Check: rendert aktiv ODER reset/reauth/transient/warehouse-invalid je nach
      // Vario-Zustand (Kanal/Lager/Token). Befuellt das Lager-Select aus der bereits geholten Liste.
      void runEntryHealthCheck(row);
      // Resume: laeuft schon ein Kategorie-Sync (z.B. Fenster wurde verlassen + neu geoeffnet),
      // zeigt pollCatJobs den Fortschritt wieder an und startet das Polling selbst.
      void pollCatJobs();
    } else {
      renderEmpty();
    }
  } catch (e) {
    // KEIN Leer-Zustand/„anlegen"-Button bei Ladefehler — sonst koennte trotz existierendem
    // Kanal erneut „anlegen" geklickt werden. Nur Fehlerhinweis; der Status laedt beim Reload neu.
    loadingEl.hidden = false;
    loadingEl.innerHTML = `<span class="sc-loading">${OTTO_ICON.info}Status konnte nicht geladen werden — bitte neu laden.</span>`;
    emptyEl.hidden = true;
    formEl.hidden = true;
    setFoot('error', `${OTTO_ICON.info}${ottoEscapeHtml((e as Error).message)}`);
  }
}
